import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  User,
  DollarSign,
  BarChart3,
  Lock,
  Globe,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import authService from '../services/authService';

const ClientForm = ({
  open,
  onClose,
  onSubmit,
  client = null,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    contact_info: {
      primary_contact: '',
      email: '',
      phone: '',
      address: ''
    },
    business_info: {
      industry: '',
      company_size: '',
      credit_rating: ''
    },
    preferences: {
      preferred_currency: 'CNY',
      payment_terms: '',
      discount_tier: '',
      tax_rate: 0.13
    },
    status: 'active',
    is_confidential: false,
    is_domestic: true
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, callback: null });

  // Check if current user is admin
  const isAdmin = authService.isAdmin();

  // Industry options
  const industryOptions = [
    '汽车制造',
    '电子制造',
    '机械制造',
    '化工',
    '医疗器械',
    '食品加工',
    '纺织',
    '建材',
    '其他'
  ];

  // Company size options
  const companySizeOptions = [
    { value: 'small', label: '小型企业 (<100人)' },
    { value: 'medium', label: '中型企业 (100-500人)' },
    { value: 'large', label: '大型企业 (>500人)' }
  ];

  // Credit rating options
  const creditRatingOptions = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC', 'CC', 'C'];

  // Discount tier options
  const discountTierOptions = [
    { value: 'gold', label: '金牌客户 (5-10%)' },
    { value: 'silver', label: '银牌客户 (3-5%)' },
    { value: 'bronze', label: '铜牌客户 (1-3%)' },
    { value: 'standard', label: '标准客户 (0-1%)' }
  ];

  // Payment terms options
  const paymentTermsOptions = [
    '现金',
    '15天付款',
    '30天付款',
    '45天付款',
    '60天付款',
    '90天付款',
    '月结',
    '季结'
  ];

  useEffect(() => {
    if (client) {
      setFormData({
        client_id: client.client_id || '',
        client_name: client.client_name || '',
        contact_info: {
          primary_contact: client.contact_info?.primary_contact || '',
          email: client.contact_info?.email || '',
          phone: client.contact_info?.phone || '',
          address: client.contact_info?.address || ''
        },
        business_info: {
          industry: client.business_info?.industry || '',
          company_size: client.business_info?.company_size || '',
          credit_rating: client.business_info?.credit_rating || ''
        },
        preferences: {
          preferred_currency: client.preferences?.preferred_currency || 'CNY',
          payment_terms: client.preferences?.payment_terms || '',
          discount_tier: client.preferences?.discount_tier || '',
          tax_rate: client.preferences?.tax_rate || 0.13
        },
        status: client.status || 'active',
        is_confidential: client.is_confidential || false,
        is_domestic: client.is_domestic !== false
      });
    } else {
      // Reset form for new client
      setFormData({
        client_id: '',
        client_name: '',
        contact_info: {
          primary_contact: '',
          email: '',
          phone: '',
          address: ''
        },
        business_info: {
          industry: '',
          company_size: '',
          credit_rating: ''
        },
        preferences: {
          preferred_currency: 'CNY',
          payment_terms: '',
          discount_tier: '',
          tax_rate: 0.13
        },
        status: 'active',
        is_confidential: false,
        is_domestic: true
      });
    }
    setErrors({});
    setSubmitError('');
  }, [client, open]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.client_id.trim()) {
      newErrors.client_id = '客户编号为必填项';
    }
    if (!formData.client_name.trim()) {
      newErrors.client_name = '客户名称为必填项';
    }
    if (!formData.contact_info.primary_contact.trim()) {
      newErrors.primary_contact = '主要联系人为必填项';
    }

    // Email validation
    if (formData.contact_info.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_info.email)) {
      newErrors.email = '请输入有效的邮箱地址';
    }

    // Phone validation (basic Chinese phone number format)
    if (formData.contact_info.phone && !/^(\+86)?[1-9]\d{10}$/.test(formData.contact_info.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = '请输入有效的手机号码';
    }

    // Tax rate validation
    if (formData.preferences.tax_rate < 0 || formData.preferences.tax_rate > 1) {
      newErrors.tax_rate = '税率应在0-1之间';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    // Check if confidentiality is being changed
    const confidentialityChanging = client &&
      (client.is_confidential !== formData.is_confidential);

    if (confidentialityChanging && isAdmin) {
      // Show confirmation dialog
      setConfirmDialog({
        open: true,
        callback: async () => {
          try {
            await onSubmit(formData);
            onClose();
          } catch (error) {
            setSubmitError(error.message || '提交失败，请重试');
          }
        }
      });
    } else {
      try {
        await onSubmit(formData);
        onClose();
      } catch (error) {
        setSubmitError(error.message || '提交失败，请重试');
      }
    }
  };

  const handleClose = () => {
    setErrors({});
    setSubmitError('');
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {client ? '编辑客户信息' : '新增客户'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {submitError && (
                <Alert variant="destructive">
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              {/* Basic Information Section */}
              <div>
                <Badge className="mb-4">基本信息</Badge>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_id">客户编号 *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="client_id"
                        value={formData.client_id}
                        onChange={(e) => handleInputChange('client_id', e.target.value)}
                        className={`pl-10 ${errors.client_id ? 'border-red-500' : ''}`}
                        disabled={!!client}
                      />
                    </div>
                    <p className={`text-xs ${errors.client_id ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {errors.client_id || '例如: CLIENT_001'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="client_name">客户名称 *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="client_name"
                        value={formData.client_name}
                        onChange={(e) => handleInputChange('client_name', e.target.value)}
                        className={`pl-10 ${errors.client_name ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {errors.client_name && (
                      <p className="text-xs text-destructive">{errors.client_name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>客户状态</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => handleInputChange('status', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">活跃</SelectItem>
                        <SelectItem value="inactive">非活跃</SelectItem>
                        <SelectItem value="pending">待审核</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Confidentiality Setting - Admin Only */}
              {isAdmin && (
                <Card className={formData.is_confidential ? 'bg-warning/10 border-warning/20' : 'bg-muted'}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.is_confidential}
                          onCheckedChange={(checked) => handleInputChange('is_confidential', checked)}
                        />
                        <div>
                          <p className="font-medium">
                            {formData.is_confidential ? "保密客户" : "普通客户"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formData.is_confidential
                              ? "仅管理员可查看此客户及相关数据"
                              : "所有用户可查看此客户"
                            }
                          </p>
                        </div>
                      </div>
                      {formData.is_confidential ? (
                        <Lock className="h-5 w-5 text-warning" />
                      ) : (
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>

                    {formData.is_confidential && (
                      <Alert className="mt-4 bg-warning/20 border-yellow-300">
                        <AlertTriangle className="h-4 w-4 text-warning" />
                        <AlertDescription className="text-warning">
                          <p className="font-medium">保密模式已启用</p>
                          <ul className="list-disc list-inside text-xs mt-1">
                            <li>仅管理员可查看此客户</li>
                            <li>相关报价单、订单将被隐藏</li>
                            <li>无法被非管理员搜索或选择</li>
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
              </Card>
              )}

              {/* Trade Type Setting - Domestic/Import */}
              <Card className={formData.is_domestic ? 'bg-blue-50/50 border-blue-200' : 'bg-purple-50/50 border-purple-200'}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={formData.is_domestic}
                        onCheckedChange={(checked) => handleInputChange('is_domestic', checked)}
                      />
                      <div>
                        <p className="font-medium">
                          {formData.is_domestic ? "国内客户" : "进口客户"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formData.is_domestic
                            ? "该客户的报价单和订单默认为国内业务"
                            : "该客户的报价单和订单默认为进口业务"
                          }
                        </p>
                      </div>
                    </div>
                    <Globe className={formData.is_domestic ? "h-5 w-5 text-blue-500" : "h-5 w-5 text-purple-500"} />
                  </div>
                </CardContent>
              </Card>


              {/* Contact Information Section */}
              <div>
                <Badge variant="secondary" className="mb-4">联系信息</Badge>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary_contact">主要联系人 *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="primary_contact"
                        value={formData.contact_info.primary_contact}
                        onChange={(e) => handleInputChange('primary_contact', e.target.value, 'contact_info')}
                        className={`pl-10 ${errors.primary_contact ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {errors.primary_contact && (
                      <p className="text-xs text-destructive">{errors.primary_contact}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">邮箱地址</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.contact_info.email}
                        onChange={(e) => handleInputChange('email', e.target.value, 'contact_info')}
                        className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">联系电话</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        value={formData.contact_info.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value, 'contact_info')}
                        className={`pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                      />
                    </div>
                    <p className={`text-xs ${errors.phone ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {errors.phone || '格式: +86-138-0000-0000'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">地址</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="address"
                        value={formData.contact_info.address}
                        onChange={(e) => handleInputChange('address', e.target.value, 'contact_info')}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Information Section */}
              <div>
                <Badge variant="outline" className="mb-4 bg-success/10 text-success border-green-300">企业信息</Badge>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>行业分类</Label>
                    <Select
                      value={formData.business_info.industry}
                      onValueChange={(value) => handleInputChange('industry', value, 'business_info')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="选择行业" />
                      </SelectTrigger>
                      <SelectContent>
                        {industryOptions.map((industry) => (
                          <SelectItem key={industry} value={industry}>
                            {industry}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>企业规模</Label>
                    <Select
                      value={formData.business_info.company_size}
                      onValueChange={(value) => handleInputChange('company_size', value, 'business_info')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="选择规模" />
                      </SelectTrigger>
                      <SelectContent>
                        {companySizeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>信用等级</Label>
                    <div className="relative">
                      <BarChart3 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                      <Select
                        value={formData.business_info.credit_rating}
                        onValueChange={(value) => handleInputChange('credit_rating', value, 'business_info')}
                      >
                        <SelectTrigger className="pl-10">
                          <SelectValue placeholder="选择等级" />
                        </SelectTrigger>
                        <SelectContent>
                          {creditRatingOptions.map((rating) => (
                            <SelectItem key={rating} value={rating}>
                              {rating}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preferences Section */}
              <div>
                <Badge variant="outline" className="mb-4 bg-warning/10 text-warning border-yellow-300">商务偏好</Badge>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>首选货币</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                      <Select
                        value={formData.preferences.preferred_currency}
                        onValueChange={(value) => handleInputChange('preferred_currency', value, 'preferences')}
                      >
                        <SelectTrigger className="pl-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CNY">人民币 (CNY)</SelectItem>
                          <SelectItem value="USD">美元 (USD)</SelectItem>
                          <SelectItem value="EUR">欧元 (EUR)</SelectItem>
                          <SelectItem value="JPY">日元 (JPY)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>付款条件</Label>
                    <Select
                      value={formData.preferences.payment_terms}
                      onValueChange={(value) => handleInputChange('payment_terms', value, 'preferences')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="选择付款条件" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTermsOptions.map((term) => (
                          <SelectItem key={term} value={term}>
                            {term}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>折扣等级</Label>
                    <Select
                      value={formData.preferences.discount_tier}
                      onValueChange={(value) => handleInputChange('discount_tier', value, 'preferences')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="选择折扣等级" />
                      </SelectTrigger>
                      <SelectContent>
                        {discountTierOptions.map((tier) => (
                          <SelectItem key={tier.value} value={tier.value}>
                            {tier.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tax_rate">税率</Label>
                    <div className="relative">
                      <Input
                        id="tax_rate"
                        type="number"
                        value={formData.preferences.tax_rate}
                        onChange={(e) => handleInputChange('tax_rate', parseFloat(e.target.value) || 0, 'preferences')}
                        step="0.01"
                        min="0"
                        max="1"
                        className={`pr-8 ${errors.tax_rate ? 'border-red-500' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                    </div>
                    <p className={`text-xs ${errors.tax_rate ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {errors.tax_rate || '输入小数形式，如0.13表示13%'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                取消
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {loading ? '处理中...' : (client ? '更新' : '创建')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Confidentiality Changes */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, callback: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              确认更改客户保密状态
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {formData.is_confidential ? (
              <div>
                <Alert className="mb-4 bg-warning/10 border-warning/20">
                  <AlertDescription className="font-medium text-warning">
                    您正在将此客户设置为保密客户
                  </AlertDescription>
                </Alert>
                <p className="text-sm mb-3">设置为保密客户后，将产生以下影响：</p>
                <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                  <li>只有管理员能够查看此客户</li>
                  <li>非管理员用户无法在客户选择器中看到此客户</li>
                  <li>相关的报价单、销售订单和采购订单将被隐藏</li>
                  <li>数据导出时将不包含此客户信息</li>
                </ul>
              </div>
            ) : (
              <div>
                <Alert className="mb-4 bg-primary/10 border-primary/20">
                  <AlertDescription className="font-medium text-primary">
                    您正在取消此客户的保密状态
                  </AlertDescription>
                </Alert>
                <p className="text-sm mb-3">取消保密状态后，将产生以下影响：</p>
                <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                  <li>所有用户都能查看此客户</li>
                  <li>客户将出现在所有用户的选择器中</li>
                  <li>相关数据将对所有有权限的用户可见</li>
                </ul>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">
              此操作将记录在系统审计日志中。
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, callback: null })}
            >
              取消
            </Button>
            <Button
              variant={formData.is_confidential ? 'default' : 'default'}
              className={formData.is_confidential ? 'bg-warning hover:bg-warning' : ''}
              onClick={() => {
                setConfirmDialog({ open: false, callback: null });
                if (confirmDialog.callback) {
                  confirmDialog.callback();
                }
              }}
            >
              {formData.is_confidential ? <Lock className="h-4 w-4 mr-2" /> : <Building2 className="h-4 w-4 mr-2" />}
              确认{formData.is_confidential ? '设为保密' : '取消保密'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientForm;
